import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link, useSearchParams } from "react-router-dom";
import {
  getWarehouseOverview,
  WarehouseOverviewItem,
  WarehouseOverviewOrderLimitTile,
  WarehouseOverviewResponse,
  WarehouseOverviewView,
} from "api/RemnantsManage";

const { Title, Text } = Typography;

const VIEW_OPTIONS: { value: WarehouseOverviewView; label: string }[] = [
  { value: "top_reserve", label: "Топ резерв" },
  { value: "top_remnant", label: "Большой остаток" },
  { value: "remnant_asc", label: "Малый остаток" },
];

const VIEW_SET = new Set(VIEW_OPTIONS.map((v) => v.value));
const DEFAULT_VIEW: WarehouseOverviewView = "top_reserve";
/** ~antd small row; scroll after more than 10 body rows */
const TABLE_BODY_ROW_PX = 39;
const TABLE_SCROLL_Y = TABLE_BODY_ROW_PX * 10;

const parseViewsFromUrl = (
  sp: URLSearchParams
): Record<number, WarehouseOverviewView> => {
  const out: Record<number, WarehouseOverviewView> = {};
  sp.forEach((value, key) => {
    const m = /^w(\d+)$/.exec(key);
    if (!m) return;
    const id = Number(m[1]);
    if (!Number.isFinite(id) || id <= 0) return;
    out[id] = VIEW_SET.has(value as WarehouseOverviewView)
      ? (value as WarehouseOverviewView)
      : DEFAULT_VIEW;
  });
  return out;
};

const viewsToApiParam = (views: Record<number, WarehouseOverviewView>) =>
  Object.entries(views)
    .map(([id, v]) => `${id}:${v || DEFAULT_VIEW}`)
    .join(",");

const manageUrl = (opts: { warehouseId: number; search?: string }) => {
  const params = new URLSearchParams();
  params.set("warehouse_id", String(opts.warehouseId));
  if (opts.search) params.set("search", opts.search);
  return `/remnants/manage?${params.toString()}`;
};

const tileStyles = (tile: WarehouseOverviewOrderLimitTile): React.CSSProperties => {
  if (!tile.is_active) {
    return {
      borderColor: "#bfbfbf",
      background: "#f5f5f5",
    };
  }
  if (tile.tripped) {
    return {
      borderColor: "#ff4d4f",
      background: "#fff2f0",
    };
  }
  return {
    borderColor: "#52c41a",
    background: "#f6ffed",
  };
};

const LimitTileCard: React.FC<{ tile: WarehouseOverviewOrderLimitTile }> = ({
  tile,
}) => {
  const fmt =
    tile.limit != null && tile.limit > 0
      ? `${tile.used} / ${tile.limit}`
      : `${tile.used} / —`;
  const fromSupply = Number(tile.used_from_supply) || 0;
  const fromRules = Number(tile.used_from_rules) || 0;
  const showSplit = fromSupply > 0 || fromRules > 0 || tile.used > 0;

  return (
    <Card
      size="small"
      style={{
        ...tileStyles(tile),
        borderWidth: 2,
        height: "100%",
      }}
      bodyStyle={{ padding: "12px 14px" }}
    >
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        <Space wrap size={[4, 4]}>
          <Tag
            color={
              tile.kind === "self_meta"
                ? "geekblue"
                : tile.kind === "meta"
                ? "orange"
                : "blue"
            }
          >
            {tile.kind === "self_meta"
              ? tile.is_default
                ? "Default физ"
                : "Физ meta"
              : tile.kind === "meta"
              ? tile.is_default
                ? "Default"
                : "Meta"
              : "Meta"}
          </Tag>
          {tile.marketplace && (
            <Tag>{tile.marketplace === "wildberries" ? "WB" : tile.marketplace}</Tag>
          )}
          <Tag color={tile.is_active ? "success" : "default"}>
            {tile.is_active ? "включён" : "выключен"}
          </Tag>
          {tile.tripped && <Tag color="error">лимит</Tag>}
        </Space>
        <Text strong style={{ fontSize: 14 }}>
          {tile.name}
        </Text>
        <Text style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.2 }}>
          {fmt}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          единицы за день
        </Text>
        {showSplit && (
          <Space size={4} wrap>
            <Tag color="cyan" style={{ margin: 0 }}>
              в поставках {fromSupply}
            </Tag>
            <Tag style={{ margin: 0 }}>
              по правилам {fromRules}
            </Tag>
          </Space>
        )}
        <Link to={tile.settings_path} style={{ fontSize: 12 }}>
          Настроить лимит →
        </Link>
      </Space>
    </Card>
  );
};

const WarehouseOverview: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlViews = useMemo(
    () => parseViewsFromUrl(searchParams),
    [searchParams]
  );

  const [data, setData] = useState<WarehouseOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const viewsParam = viewsToApiParam(urlViews);
      const res = await getWarehouseOverview(
        viewsParam ? { views: viewsParam } : undefined
      );
      setData(res);
    } catch {
      setError("Не удалось загрузить обзор складов");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [urlViews]);

  useEffect(() => {
    load();
  }, [load]);

  const setWarehouseView = (
    warehouseId: number,
    view: WarehouseOverviewView
  ) => {
    const next = new URLSearchParams();
    searchParams.forEach((val, key) => {
      if (/^w\d+$/.test(key)) return;
      next.set(key, val);
    });
    const nextViews = { ...urlViews, [warehouseId]: view };
    for (const [id, v] of Object.entries(nextViews)) {
      if (v && v !== DEFAULT_VIEW) next.set(`w${id}`, v);
    }
    setSearchParams(next, { replace: true });
  };

  const columnsFor = (
    warehouseId: number,
    _view: WarehouseOverviewView
  ): ColumnsType<WarehouseOverviewItem> => {
    const base: ColumnsType<WarehouseOverviewItem> = [
      {
        title: "Артикул",
        dataIndex: "article",
        key: "article",
        width: 110,
        ellipsis: true,
        render: (article: string, row) => (
          <Space size={4} wrap={false}>
            <Link to={manageUrl({ warehouseId, search: article })}>
              {article}
            </Link>
            {row.is_combo && <Tag style={{ margin: 0 }}>комбо</Tag>}
          </Space>
        ),
      },
      {
        title: "Название",
        dataIndex: "name",
        key: "name",
        width: 140,
        ellipsis: true,
        render: (v: string | null) => v || "—",
      },
      {
        title: "Резерв",
        dataIndex: "reserved_mp",
        key: "reserved_mp",
        width: 72,
        align: "right",
      },
      {
        title: "1С",
        dataIndex: "remnant_1c",
        key: "remnant_1c",
        width: 72,
        align: "right",
        render: (v: number, row) => (row.is_combo ? `${v}*` : v),
      },
    ];
    return base;
  };

  const tiles = data?.order_limit_tiles || [];

  return (
    <div style={{ width: "100%", paddingBottom: 24 }}>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
        wrap
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Остатки по физскладам
          </Title>
          <Text type="secondary">
            Лимиты единиц и топ товаров
            {data?.day ? ` · день ${data.day} (МСК)` : ""}
          </Text>
        </div>
        <Button onClick={load} loading={loading}>
          Обновить
        </Button>
      </Space>

      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 12 }} />
      )}

      <Spin spinning={loading}>
        {tiles.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ marginBottom: 8 }}>
              Лимиты единиц по складам
            </Title>
            <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
              Зелёный — включён · серый — выключен · красный — лимит исчерпан.
              «В поставках» — exact по маркеру ИГ/МШ; «по правилам» — waterfall /
              единственная связь. Meta-группы МП:{" "}
              <Link to="/settings/auth_marketplace/wildberries">WB</Link>
              {" / "}
              <Link to="/settings/auth_marketplace/ozon">Ozon</Link>
              . Meta-группы физскладов:{" "}
              <Link to="/settings/warehouses">Склады</Link>.
            </Text>
            <Row gutter={[12, 12]}>
              {tiles.map((tile) => (
                <Col xs={24} sm={12} md={8} lg={6} key={`${tile.kind}-${tile.id}`}>
                  <LimitTileCard tile={tile} />
                </Col>
              ))}
            </Row>
          </div>
        )}

        <Row gutter={[16, 16]}>
          {(data?.warehouses || []).map((w) => {
            const view = urlViews[w.id] || w.view || DEFAULT_VIEW;
            return (
              <Col xs={24} lg={12} key={w.id}>
                <Card
                  size="small"
                  title={
                    <Space wrap>
                      <span>{w.name}</span>
                      {w.is_default && <Tag color="blue">default</Tag>}
                    </Space>
                  }
                  extra={
                    <Link to={manageUrl({ warehouseId: w.id })}>
                      В управление →
                    </Link>
                  }
                >
                  <Space wrap size={[8, 8]} style={{ marginBottom: 10 }}>
                    <Tag style={{ margin: 0 }}>
                      в резерве (заказано) {w.summary.reserved_mp_sum}
                    </Tag>
                    {w.order_limits && (
                      <>
                        <Tag color="cyan" style={{ margin: 0 }}>
                          в поставках{" "}
                          {Number(w.order_limits.used_from_supply) || 0}
                        </Tag>
                        <Tag style={{ margin: 0 }}>
                          по правилам{" "}
                          {Number(w.order_limits.used_from_rules) || 0}
                        </Tag>
                      </>
                    )}
                  </Space>
                  <Segmented
                    size="small"
                    value={view}
                    options={VIEW_OPTIONS}
                    onChange={(v) =>
                      setWarehouseView(w.id, v as WarehouseOverviewView)
                    }
                    style={{ marginBottom: 10, maxWidth: "100%" }}
                  />
                  <Table
                    size="small"
                    rowKey="article"
                    pagination={false}
                    tableLayout="fixed"
                    columns={columnsFor(w.id, view)}
                    dataSource={w.items}
                    scroll={
                      (w.items?.length || 0) > 10
                        ? { y: TABLE_SCROLL_Y }
                        : undefined
                    }
                    locale={{ emptyText: "Нет данных" }}
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      </Spin>
    </div>
  );
};

export default WarehouseOverview;
